export interface ReleaseNote {
    version: string
    title: string
    date: string
    highlights: string[]
}

/** Hardcoded release notes, newest first. */
export const RELEASE_NOTES: ReleaseNote[] = [
    {
        version: "0.83.0",
        title: "Multi-Project Task View",
        date: "2026-08-10",
        highlights: [
            "The sidebar keeps a compact project switcher visible above one merged task list, so a busy project can no longer push every other project below the fold",
            "Calendar-style project checkboxes show or hide each project's tasks from the merged list, with Show only, All, running and unread counts, and reversible one-click project archiving",
            "Pinned work stays above a project-fair 5/4/3 attention window, with compact live-status cues ending in a quiet right-edge project tile",
        ],
    },
    {
        version: "0.82.0",
        title: "Actions That Fit Your Workflow",
        date: "2026-07-26",
        highlights: [
            "Create, edit, reorder, toggle, or remove custom one-click Actions with concise messages, a restrained Tailwind palette, icon presets, and visibility rules",
            "Review separates safe fixes from clear improvements, while TLDR compresses a long response into a half-page actionable brief and Brief summarizes broader task status",
            "Review and Council now keep waiting for their nested agent to finish instead of interrupting it or proceeding from partial output",
            "Abandon preserves task work in a recoverable local commit when needed, removes its worktree, and closes without deleting branches or commits",
            "Land now fast-forward pushes a clean ahead-only default branch under direct integration policy instead of blocking on commits from other tasks",
            "Push replaces Sync with ref-aware publishing: it pushes relevant task-branch work plus a clean ahead-only default branch without creating an obsolete remote task branch after landing",
            "Compatible queued Actions—including Close Out at the end of a lifecycle sequence—now collapse into one ordered turn after 2.3 seconds, while Review, both review-remediation choices, Council, Preview, Brief, or custom context-sensitive work can stay standalone",
            "Task controls are focused on Do, Queue, Steer, Interrupt, Stop, and direct system Close; the old planning, auto-handoff review, repeat, retry, and commit-and-push workflows have been removed",
        ],
    },
    {
        version: "0.81.0",
        title: "Markdown Conversation Export",
        date: "2026-06-23",
        highlights: [
            "Task pages now include a Markdown export for sharing or archiving conversations outside WildLark",
            "You can copy the generated Markdown or download it as a .md file from a live preview",
            "Exports include user and agent messages by default, with toggles for function calls, function results, and agent thinking",
        ],
    },
    {
        version: "0.80.1",
        title: "Cleaner Agent Timelines",
        date: "2026-06-23",
        highlights: [
            "Claude thinking-token telemetry now folds into the Thinking block instead of showing as unknown timeline events",
            "Thinking blocks show approximate token counts when Claude reports them",
            "Task timeline events now start expanded by default, while snapshots stay collapsed until opened",
        ],
    },
    {
        version: "0.80.0",
        title: "Claude Fable 5",
        date: "2026-06-09",
        highlights: [
            "Claude Code now includes Claude Fable 5, Anthropic's most powerful model, as a selectable option",
            "The review picker now leads with the top models — Fable 5, Opus 4.8, and GPT-5.5 — and visually de-emphasizes the rest",
            "Haiku is no longer offered as a review agent to keep the picker focused on capable models",
        ],
    },
    {
        version: "0.79.0",
        title: "Runtime Core, Queued Turns & Markdown",
        date: "2026-05-31",
        highlights: [
            "Task execution, reviews, HyperPlan, files, git, processes, PTYs, MCP, and storage now share a runtime core for more reliable desktop and remote workflows",
            "You can queue Do and Ask follow-ups while a task is running, see queued turns in the input bar, cancel them, or interrupt the active turn to send a replacement immediately",
            "Agent messages now render richer Markdown with syntax-highlighted code, task lists, math, diagrams, safer external links, and copy controls",
            "Local file links in agent output and Markdown now resolve fuzzy, mentioned, and line-numbered paths directly into the file viewer",
            "Linux AppImage startup is more stable under Wayland, and running tasks can recover after desktop runtime restarts",
        ],
    },
    {
        version: "0.78.0",
        title: "Claude Opus 4.8",
        date: "2026-05-28",
        highlights: [
            "Claude Code now includes Claude Opus 4.8 as an explicit model option",
            "Rolling Opus fast mode no longer forces the older Opus 4.7 fast-mode override",
        ],
    },
    {
        version: "0.77.0",
        title: "Editor-Safe Shortcuts & Clearer Reviews",
        date: "2026-05-24",
        highlights: [
            "Focused editors now keep arrow-key control instead of being interrupted by global project or task navigation shortcuts",
            "Project and task shortcut arrows now match the sidebar hierarchy, with left/right for projects and up/down for tasks",
            "Agent TL;DR guidance is more flexible, using concise bullets that fit the response instead of fixed content slots",
            "Review findings now use Criticality: N/10 formatting so severity scores are easier to scan and compare",
        ],
    },
    {
        version: "0.76.0",
        title: "Keyboard Navigation & Work Recaps",
        date: "2026-05-20",
        highlights: [
            "Keyboard-first navigation now covers task creation, task switching, project switching, tray movement, command actions, and HyperPlan strategy selection",
            "Shortcut badges appear while holding the command key so available actions are easier to discover, with an option to hide the hints",
            "Stats now include copyable work recaps for selected periods, grouped by project with task status, runtime, cost, and activity details",
            "Agent transcripts now render more Claude operational events, Codex web searches, and compact file changes as readable timeline pills",
            "Release builds now smoke test packaged desktop artifacts before publishing, and Electron shell/titlebar behavior is more reliable across platforms",
        ],
    },
    {
        version: "0.75.0",
        title: "Fast Mode & Agent Event Visibility",
        date: "2026-05-17",
        highlights: [
            "Fast mode can now be toggled for supported Claude Code and Codex tasks, including new tasks, resumed runs, run commands, and HyperPlan executions",
            "Sidebar selection mode can close multiple selected open tasks, and closing works even for tasks that were not already loaded",
            "Terminal-focused keyboard shortcuts now stay with the terminal instead of being intercepted by the app window",
            "Harness file changes, todo updates, unsupported items, and unknown Codex/Claude events now render in task timelines instead of disappearing",
            "Stats usage backfill is more reliable for stale task documents, and review handoffs now include criticality scores",
        ],
    },
    {
        version: "0.74.1",
        title: "Always-Expanded Git Tray",
        date: "2026-05-04",
        highlights: [
            "Changes tray directories now stay expanded automatically so nested files are visible without extra clicks",
            "Directory collapse controls were removed for a simpler, scan-first git review flow",
        ],
    },
    {
        version: "0.74.0",
        title: "Usage Stats & Sharper Reviews",
        date: "2026-05-03",
        highlights: [
            "Stats now include agent runtime totals in task stats, monthly breakdowns, and share cards",
            "Codex usage costs are calculated more accurately with cached-token pricing and resumed-session deltas",
            "Reviews and plans now separate explicit user requirements from agent decisions more clearly",
            "The changes tray keeps newly appearing directories expanded while still respecting directories you collapsed",
            "Diff status icons stay readable in dense file lists instead of shrinking",
        ],
    },
    {
        version: "0.73.0",
        title: "Smarter Resumes & Review Diffs",
        date: "2026-04-26",
        highlights: [
            "Changing the model on a resumed Codex task now takes effect instead of silently sticking to the original model",
            "Review and changes trays now switch diff context reliably without showing stale partial patches, with bounded context options that stay responsive",
            "Review model pickers now stay aligned with the models you can actually select, and the redundant Claude Opus latest alias is hidden",
            "Closed tasks now show a GitHub-style [Closed] marker before the title so status is easier to spot",
        ],
    },
    {
        version: "0.72.1",
        title: "Claude Opus Selection Fix",
        date: "2026-04-25",
        highlights: [
            "Claude Code tasks using rolling Opus selections no longer break on newer Claude Code versions",
            "Concrete Claude model IDs still force matching subagent models when configured",
        ],
    },
    {
        version: "0.72.0",
        title: "Faster Diffs & GPT-5.5",
        date: "2026-04-24",
        highlights: [
            "Large snapshot and diff views stay responsive — snapshot bundles are indexed and only the selected file slice is loaded",
            "Changes and git log trays default to compact per-hunk diffs with shared context controls for quicker history review",
            "Added GPT-5.5 to Codex task creation, and your selected model now persists from the new-task page through to the first execution",
            'Scratchpad titles no longer revert to "Untitled" on empty first lines, and the tray remembers the last-active pad per workspace',
            "Task headers now display a Closed label so the page title reflects task status",
        ],
    },
    {
        version: "0.71.0",
        title: "Claude Opus 4.7",
        date: "2026-04-16",
        highlights: [
            "Default Opus model upgraded to Claude Opus 4.7 for improved reasoning and coding performance",
            "Existing tasks with older Opus/Sonnet full IDs continue to work — model aliases now resolve across current and future Claude releases",
        ],
    },
    {
        version: "0.70.0",
        title: "Scratchpad, Draft Stashing & Sonnet 4.6",
        date: "2026-04-13",
        highlights: [
            "New scratchpad tray panel for quick notes with auto-generated titles from the first line",
            "Stash task drafts to a side rail and restore them later — rich editor state including file mentions and images is preserved across context switches and refreshes",
            "Cron jobs can now reuse an existing task thread instead of always creating new tasks",
            "Added Sonnet 4.6 model with default effort set to max",
            "Stderr line bursts in inline messages are now collapsed for cleaner output",
        ],
    },
    {
        version: "0.69.0",
        title: "Inline Config Editing & Better Navigation",
        date: "2026-04-12",
        highlights: [
            "Edit processes and cron jobs inline from the app instead of round-tripping through a task and raw TOML edits",
            "Browse multiple wildlark.toml files from a built-in file tree with a wider editor for faster config maintenance",
            "Pinned tasks now always stay above unpinned tasks in the sidebar, even when an unpinned task is running",
            "Process config is now consistently wildlark.toml-only, reducing ambiguity across process management",
        ],
    },
    {
        version: "0.68.0",
        title: "Task Management & Harness APIs",
        date: "2026-04-12",
        highlights: [
            "Multi-select task deletion lets you bulk-remove tasks with full resource cleanup",
            "Pin tasks to the top of your sidebar for quick access to important work",
            "Edit task titles inline directly from the sidebar or navbar",
            "MCP connectors now open in a popover and reliably toggle on first click",
            "Reviews focus on blocking findings only, with new dimensions for security, style, and robustness",
        ],
    },
    {
        version: "0.67.0",
        title: "Refined Reviews",
        date: "2026-04-02",
        highlights: [
            "Reviews are less aggressive — findings now distinguish real bugs from potentially intentional behavior with a confirmation section",
            "Review picker redesigned as a single-click agent list with top models highlighted",
            "Review handoff context is now compact, preventing bloated follow-up prompts",
            "Task thread serializer no longer includes function-call inputs by default, reducing noise",
        ],
    },
    {
        version: "0.66.0",
        title: "Isolated Reviews & HyperPlan Reliability",
        date: "2026-04-02",
        highlights: [
            "New Review commands let you launch an independent read-only review of your plan or recent work with a dedicated harness and model picker",
            "Stop reliably cancels in-progress HyperPlan runs and cleans up all sub-sessions",
            "Fixed HyperPlan session resume using the wrong harness and model after handoff",
            "Stats page now includes Today and This Week period filters for quick access to recent usage",
            "Git state now refreshes automatically when the terminal tray closes",
        ],
    },
    {
        version: "0.65.1",
        title: "Cron Timer Reliability Fix",
        date: "2026-03-16",
        highlights: [
            "Fixed cron scheduler skipping the current time slot by firing directly on timer instead of recomputing the next run",
            "Refresh events (task completion, window focus) are now debounced to avoid cancelling and recreating timers on every event",
        ],
    },
    {
        version: "0.65.0",
        title: "Cron Scheduling Fix & Repeat Limits",
        date: "2026-03-12",
        highlights: [
            "Cron jobs now fire reliably on schedule with catch-up for missed runs",
            "Repeat mode now has a configurable max runs limit (default 100) to prevent runaway loops",
            "Fixed a crash in the todo list parser when a malformed non-array value was passed",
        ],
    },
    {
        version: "0.64.0",
        title: "Cron Jobs, Repeat Actions & Windows Fix",
        date: "2026-03-09",
        highlights: [
            "Schedule recurring tasks with cron jobs — define schedules in wildlark.toml and manage them from the sidebar",
            "New Repeat action lets you loop a prompt repeatedly with an optional stop-on-text condition",
            "Fixed blank screen on Windows caused by BrowserRouter on file:// protocol",
            "Tray buttons are now compact icon-only squares with tooltips, reducing input bar overflow",
            "Session forking disabled by default so follow-up turns continue in-place instead of forking",
        ],
    },
    {
        version: "0.63.0",
        title: "GPT-5.4, Frecency Favorites & Diff Performance",
        date: "2026-03-06",
        highlights: [
            "GPT-5.4 is now the default Codex model, offering improved coding performance",
            "File favorites now use frecency ranking — recently and frequently used files surface first",
            "Stale file favorites that reference deleted files are automatically pruned on load",
            "Large file diffs (lock files, generated files, 10k+ line files) no longer freeze the Changes and Git Log views",
        ],
    },
    {
        version: "0.62.0",
        title: "Thinking Controls, Stats Sharing & Sidebar Polish",
        date: "2026-03-01",
        highlights: [
            "New thinking-level picker lets you control how much reasoning effort the AI uses per task",
            "Share your usage stats as a shareable image card with one-click copy to clipboard",
            "Drag and drop images directly onto the task creation page to include them in your prompt",
            "Sidebar now offers copy-path actions for workspaces and tasks, and shows deleted files in the Changes tray",
            "Tool call pills display file names and patterns for easier scanning of what the AI touched",
        ],
    },
    {
        version: "0.61.0",
        title: "Git Log, Smart Bash Pills & Image Prompts",
        date: "2026-02-26",
        highlights: [
            "New Git Log tray lets you browse commit history, view diffs, and inspect changes without leaving the app",
            "Bash commands now render as friendly semantic pills — search, read, edit, git — instead of raw shell invocations",
            "Attach images to prompts via paste, drag-and-drop, or file picker with support across task runs",
            "Changes viewer rebuilt as a collapsible file tree with Open in Finder support",
            "Commit and push unified into a single action that accepts custom instructions from the editor",
        ],
    },
    {
        version: "0.55.0",
        title: "Plan Button Fix",
        date: "2026-02-15",
        highlights: [
            "Fixed Plan button incorrectly launching HyperPlan when a multi-agent strategy was previously selected",
            "Plan and HyperPlan are now distinct actions — choosing Plan always runs standard planning regardless of persisted strategy",
        ],
    },
    {
        version: "0.54.0",
        title: "HyperPlan & Multi-Harness Support",
        date: "2026-02-15",
        highlights: [
            "HyperPlan lets you run multiple AI agents in parallel with DAG-based planning strategies for complex tasks",
            "Multi-harness support: switch between Claude Code and Codex backends from the same interface",
            "Archive and hide workspaces from the sidebar to keep your project list tidy",
            "Global start/stop buttons in the Processes tray to manage all daemons at once",
            "Conversation context is now included when regenerating task titles for more accurate results",
        ],
    },
    {
        version: "0.53.0",
        title: "Push to PR & Extended Thinking",
        date: "2026-02-10",
        highlights: [
            "New Push command that automatically creates pull requests with LLM-generated titles and descriptions",
            "Claude's extended thinking blocks now render inline so you can follow the reasoning process",
            "SDK tool calls and results are visually nested under their parent action for cleaner conversation flow",
            "Processes tray redesigned with compact single-line rows and hover-reveal actions",
            "Auto-update failures now show an error banner with a one-click retry option",
        ],
    },
    {
        version: "0.52.0",
        title: "Image Input & Workspace Overhaul",
        date: "2026-02-09",
        highlights: [
            "Paste, drag-and-drop, or pick images to attach to any message",
            "New 3-way workspace creation: open an existing directory, create a new one, or start from a prototype",
            "File browser, content search, and model selection are now scoped per task",
            "Edit menu in process tray for creating and updating wildlark.toml directly",
            "Unread and running indicators on workspace sidebar items with last-viewed-page memory",
        ],
    },
    {
        version: "0.51.0",
        title: "MCP Connectors & Improved Terminal",
        date: "2026-02-05",
        highlights: [
            "Added 12 new MCP connector presets for popular services",
            "Terminal now supports custom themes",
            "Fixed worktree cleanup on task deletion",
        ],
    },
    {
        version: "0.50.0",
        title: "Multi-Theme Support",
        date: "2026-01-28",
        highlights: [
            "6 new themes: Light, Bright, Clean, Black, Synthwave, Dracula",
            "Theme picker in settings with live preview",
            "System theme auto-detection",
        ],
    },
    {
        version: "0.49.0",
        title: "Collaborative Workspaces",
        date: "2026-01-20",
        highlights: ["Real-time collaboration with shared cursors", "Per-task comment threads", "Improved onboarding flow for new users"],
    },
]
